/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef, useState } from 'react';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { cssMap, cx, jsx } from '@atlaskit/css';
import DragHandlerIcon from '@atlaskit/icon/utility/drag-handle';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { token } from '@atlaskit/tokens';
import { ModalEntryPointDropdownItemTrigger } from '@atlassian/jira-modal-entry-point-dropdown-item-trigger/src/ModalEntryPointDropdownItemTrigger.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ROW_HEIGHT, ROW_OPERATION_REORDER } from '../../../../common/constants.tsx';
import { RowDropIndicator } from '../../../../common/ui/row-drop-indicator/index.tsx';
import { RowPopupButton } from '../../../../common/ui/row-popup-button/index.tsx';
import { useDraggableRows } from '../../../../controllers/draggable-rows/index.tsx';
import type {
	DropTarget,
	DraggableRowProps,
	DropEdgeTarget,
} from '../../../../controllers/draggable-rows/types.tsx';
import { reparentModalEntryPoint } from '../../../reparent-modal/entrypoint.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	button: {
		height: '16px',
		width: '14px',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.raised'),
		color: token('color.text'),
		// Change to 'pointer' if we choose to implement issue actions dropdown
		cursor: 'grab',
		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.blue.subtle.pressed'),
			color: token('color.link.pressed'),
			cursor: 'grabbing',
		},
	},
});

// Negatively offset by 1px to account for the cell border
const offset: [number, number] = [ROW_HEIGHT / 2 - 1, -1];

const BUTTON_SIZE = 16;

type DragButtonState = 'idle' | 'dragging';

// TODO: Remove when cleaning jira_list_reparenting
const isDropEdgeTarget = (dropTarget: DropTarget): dropTarget is DropEdgeTarget =>
	'dropEdge' in dropTarget;

export type Props = DraggableRowProps & {
	issueKey?: string | null | undefined;
	projectKey?: string | undefined;
	isRowHovered?: boolean;
};

export const DragActionsButton = ({
	edgeIndex,
	issueId,
	hierarchyLevel,
	parentIssueAri,
	issueKey,
	projectKey,
	isRowHovered,
}: Props) => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [buttonState, setButtonState] = useState<DragButtonState>('idle');

	const [{ currentDropTarget, instanceId }] = useDraggableRows();
	const { formatMessage } = useIntl();

	const isVisible = isRowHovered || buttonState === 'dragging';
	const isDropTarget = currentDropTarget?.issueId === issueId;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();

	useEffect(() => {
		if (!buttonRef.current) return;

		return draggable({
			element: buttonRef.current,
			getInitialData() {
				return {
					operation: ROW_OPERATION_REORDER,
					instanceId,
					issueId,
					rowIndex: edgeIndex,
					hierarchyLevel,
					parentIssueAri,
				};
			},
			onDragStart() {
				setButtonState('dragging');
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'dragged',
						actionSubject: 'dragActionsButton',
						actionSubjectId: 'jscDraggableIssue',
					}),
				);
			},
			onDrop() {
				buttonRef.current?.blur();
				setButtonState('idle');
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'dropped',
						actionSubject: 'dragActionsButton',
						actionSubjectId: 'jscDraggableIssue',
					}),
				);
			},
		});
	}, [createAnalyticsEvent, edgeIndex, issueId, hierarchyLevel, parentIssueAri, instanceId]);

	return (
		<>
			{expVal('jira_list_reparenting', 'isReparentingEnabled', false) ? (
				<DropdownMenu
					trigger={({ triggerRef, ...triggerProps }) => (
						<RowPopupButton
							alignToYAxis
							buttonSize={BUTTON_SIZE}
							icon={<DragHandlerIcon label="" />}
							isVisible={isVisible}
							label={formatMessage(messages.label)}
							offset={offset}
							ref={mergeRefs(triggerRef, buttonRef)}
							xcss={cx(styles.button)}
							onClick={triggerProps.onClick}
						/>
					)}
				>
					<ModalEntryPointDropdownItemTrigger
						entryPoint={reparentModalEntryPoint}
						entryPointProps={{
							issueId,
							parentIssueAri,
							issueKey,
							hierarchyLevel,
						}}
						entryPointParams={{
							cloudId,
							projectKey,
						}}
						interactionName="open-reparent-issue-modal"
						useInternalModal
					>
						{parentIssueAri
							? formatMessage(messages.changeParent)
							: formatMessage(messages.addParent)}
					</ModalEntryPointDropdownItemTrigger>
				</DropdownMenu>
			) : (
				<RowPopupButton
					alignToYAxis
					buttonSize={BUTTON_SIZE}
					icon={<DragHandlerIcon label="" />}
					isVisible={isVisible}
					label={formatMessage(messages.label)}
					offset={offset}
					ref={buttonRef}
					xcss={cx(styles.button)}
				/>
			)}
			{isDropTarget && (
				<RowDropIndicator
					{...(isDropEdgeTarget(currentDropTarget)
						? {
								edge: currentDropTarget.dropEdge,
							}
						: { instruction: currentDropTarget.dropInstruction })}
				/>
			)}
		</>
	);
};
